import React, { useEffect, useState, useCallback } from "react";
import "card-pattern/dist/main.css";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import Loader from "../../../components/common/Loader";
import Table, {
  TableColumn,
  TableRow,
} from "../../../components/common/Table/Table";
import CardTable from "../../../components/common/CardTable";
import { useNavigate } from "react-router";
import Pagination from "../../../components/common/Table/Pagination";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useProfile } from "../../../contexts/ProfileContext";
import { Link, useLocation } from "react-router-dom";
import formatDate from "../../../utils/formatDate";
import {
  fetchHistoryUBTList,
  setCurrentPage,
} from "../../../store/slices/historySlice";
import { formatTimestampMDY } from "../../../utils/date";
import BreadCrumbs from "../../../components/common/BreadCrumbs";
import { teamStorage } from "../../../utils/teamStorage";
import Notifications from "../../../components/common/Notifications";


const columns: TableColumn[] = [
  { key: "teamId", label: "ID" },
  { key: "teamName", label: "Name of UBT", className: "md:max-w-[7rem]" },
  {
    key: "serviceDeliveryType",
    label: "Service type",
    className: "md:max-w-[6rem]",
  },
  { key: "usingHuddles", label: "Using huddles", className: "md:max-w-[6rem]" },
  {
    key: "learningPlanRefreshDt",
    label: "Last face-to-face assessment",
    className: "md:max-w-[8rem]",
  },
  {
    key: "teamDateCreated",
    label: "Date team established",
    className: "md:max-w-[8rem]",
  },
  {
    key: "teamStopDate",
    label: "Date team closed",
    className: "md:max-w-[8rem]",
  },
  {
    key: "likertScaleRating",
    label: "Team Rating",
    className: "md:max-w-[8rem]",
  },
  {
    key: "teamDateLastUpdated",
    label: "Last updated",
    className: "md:max-w-[8rem]",
    isSortable: true,
  },
  {
    key: "updatedBy",
    label: "Updated by",
    className: "md:max-w-[6rem]",
    isSortable: true,
  },
];

const History: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loading: profileLoading } = useProfile();

  const {
    historyUBTList,
    currentPage,
    perPage,
    totalItems,
    loading: historyLoading,
  } = useAppSelector((state) => state.history);

  const location = useLocation();
  const stateTeamId = (location.state as { teamId?: string | number } | null)?.teamId;
  const selectedTeamId: string | null =
    stateTeamId == null
      ? teamStorage.getTeamId()
      : String(stateTeamId);

  const navigate = useNavigate();

  const [sortColumn, setSortColumn] = useState<string>("teamDateLastUpdated");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");


  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(1));
    };
  }, [dispatch]);

  useEffect(() => {
    const mapSortColumnToApi = (col?: string) => {
      if (!col) return undefined;
      if (col === "updatedBy") return "LAST_NAME";
      if (col === "teamDateLastUpdated") return "TEAMDATELASTUPDATED";
      return col.toUpperCase();
    };

    const apiSortBy = mapSortColumnToApi(sortColumn);
    const apiSortOrder = sortDirection
      ? sortDirection.toUpperCase()
      : undefined;

    dispatch(
      fetchHistoryUBTList({
        page: currentPage,
        perPage,
        sortBy: apiSortBy,
        sortOrder: apiSortOrder,
        teamId: selectedTeamId,
      }),
    );
  }, [currentPage, perPage, dispatch, sortColumn, sortDirection]);

  const rows: TableRow[] = historyUBTList.map((row: any) => {
    const last = row.lastName ?? "";
    const first = row.firstName ?? "";
    const middle = row.middleName ?? "";
    const combinedName = [last, first, middle].filter(Boolean).join(" ");
    const updatedByValue = combinedName ?? "";
    const newRow: any = { ...row, updatedBy: updatedByValue };
    // Ensure team name wraps properly in table cells
    newRow.teamName = (
      <div className="max-w-[110px] whitespace-normal break-words overflow-wrap-[anywhere]">
        {row.teamName ?? ""}
      </div>
    );
    delete newRow.firstName;
    delete newRow.lastName;
    delete newRow.middleName;
    // use shared util for date formatting

    const dateKeys = [
      "learningPlanRefreshDt",
      "teamDateCreated",
      "teamStopDate",
      "teamDateLastUpdated",
      "lastUpdated",
    ];
    // teamDateCreated/teamStopDate are date-only strings; formatting them via
    // formatDate (new Date()) would shift the day in negative-UTC timezones.
    const dateOnlyKeys = new Set(["teamDateCreated", "teamStopDate"]);
    dateKeys.forEach((k) => {
      if (newRow[k]) {
        // choose explicit format per key to make behaviour predictable
        if (k === "learningPlanRefreshDt") {
          newRow[k] = formatTimestampMDY(newRow[k]);
        } else if (dateOnlyKeys.has(k)) {
          newRow[k] = formatDate(newRow[k], "MM-DD-YYYY HH:MM:SS");
        } else {
          newRow[k] = formatDate(newRow[k], "MM-DD-YYYY HH:MM:SS");
        }
      }
    });
    return newRow;
  });

  const handleBreadcrumbPrevious = useCallback((): void => {
    navigate("/home");
  }, [navigate]);

  const ubtNameForBreadcrumb = (historyUBTList[0]?.teamName ?? "").trim();
  const truncatedUbtName =
    ubtNameForBreadcrumb.length > 25
      ? `${ubtNameForBreadcrumb.slice(0, 25)}...`
      : ubtNameForBreadcrumb;
  const projectsCrumbLabel = truncatedUbtName
    ? `Projects: ${truncatedUbtName}`
    : "Projects";

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  const handleSort = useCallback(
    (columnKey: string, direction: "ASC" | "DESC") => {
      setSortColumn(columnKey);
      setSortDirection(direction);
    },
    [],
  );

  // Show loader while profile is loading, or when a sort is active and history API is loading
  if (profileLoading || (sortColumn && historyLoading)) return <Loader />;

  if (!selectedTeamId) {
    return (
      <div className="min-h-[70vh] lg:mx-40! mx-5!">
        <div className="mb-8!">
          <BreadCrumbs
            items={[
              { label: 'UBT Home', path: '/home', onClick: handleBreadcrumbPrevious },
              { label: 'History' },
            ]}
          />
        </div>
        <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">UBT History</h1>
        <div className="mt-8! max-w-[80%]">
          <Notifications
            heading="Please select an UBT"
            variant="Alert"
            messageInfo="Please select an UBT before accessing History page. You can access the page from Select UBT/ My Teams etc."
          >
            <ul className="flex items-start list-disc m-0 my-0! ml-4 mt-5!">
              <li>
                <Link
                  to="/home/select-ubt"
                  data-analytics-type="link"
                  className="text-kp-blue-dark underline text-left text-[16px]! w-full bg-transparent border-none p-0 cursor-pointer hover:text-kp-blue"
                  aria-label="Navigate to Select a UBT page"
                >
                  Select a UBT
                </Link>
              </li>
            </ul>
          </Notifications>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <div className="flex justify-between items-end ">
        <div className="mobile-margin  ">
          <div className="mb-8!">
          <BreadCrumbs
            items={[
              {
                label: 'UBT Home',
                path: '/home',
                onClick: handleBreadcrumbPrevious,
              },
              {
                label: `${projectsCrumbLabel}`,
                path: '/home/projects',
              },
              {
                label: 'History',
              },
            ]}
          />
        </div>

        </div>

        <div className="mobile-margin lg:mt-15! ">
          <div className="about-this-team" style={{ color: '#677083' }}> About this team :</div>
          <Link
            to="/home/path-to-performance"
            state={{ teamId: selectedTeamId, from: 'History'}}
            data-analytics-type="link"
            className="no-underline! p1 -medium"
          >
            P2P
          </Link>
          <Link
            to="/home/members"
            state={{ teamId: selectedTeamId, from: 'History' }}
            data-analytics-type="link"
            className="no-underline! p1 -medium pl-6! "
          >
            Members
          </Link>
          <Link
            to="/home/projects"
            state={{ teamId: selectedTeamId, from: 'History' }}
            data-analytics-type="link"
            className="no-underline! p1 -medium pl-6!"
          >
            Projects
          </Link>
        </div>
      </div>

      <div className="flex justify-between items-end mb-4! md:mb-0! desktop:pb-4!  margin-top-4u desktop:mt-12! md:mt-0! mobile:mt-3.5!">
        <div className="">
          <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">UBT History</h1>
          <h2 className="h2 tablet:mt-8! mobile:mt-3.5! w-full max-w-full whitespace-normal break-all [overflow-wrap:anywhere]">
            {ubtNameForBreadcrumb}
          </h2>
        </div>
      </div>

      <div>
        <CardTable>
          {rows.length > 0 ? (
            <>
              <Table
                columns={columns}
                rows={rows}
                showPagination={true}
                onSort={handleSort}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
              />
              <div className="mt-4 left-0 mobile:relative mobile:left-7.5">
                <Pagination
                  totalItems={totalItems}
                  itemsPerPage={perPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          ) : (
            <p className="p3 -book text-center mt-2.5! desktop:mt-50!">
              No applications found.
            </p>
          )}
        </CardTable>
      </div>

      <div className="mb-10!">
        <div className="-mb-27! tablet:hidden">
          <ScrollToTopBtn />
        </div>
      </div>
    </div>
  );
};

export default History;
